/**
 * Typed access to the Vite build-time environment. Anything read here is baked into the
 * bundle and therefore public — never put a secret in a VITE_* variable.
 */

export interface AppEnv {
  apiBaseUrl: string;
  cognitoAuthority: string;
  cognitoClientId: string;
  cognitoDomain: string;
  redirectUri: string;
  logoutUri: string;
  /** True only in a dev server run with VITE_LOCAL_AUTH_BYPASS=true. */
  authBypass: boolean;
}

function readString(value: string | undefined, fallback = ''): string {
  const trimmed = (value ?? '').trim();
  return trimmed.length > 0 ? trimmed : fallback;
}

function stripTrailingSlash(value: string): string {
  return value.endsWith('/') ? value.slice(0, -1) : value;
}

/**
 * The bypass is double-gated: the flag must be set AND the bundle must be a dev build.
 * `import.meta.env.DEV` is statically false in `vite build`, so this whole branch is
 * dead code that the production bundle drops.
 */
function readAuthBypass(): boolean {
  if (!import.meta.env.DEV) return false;
  return readString(import.meta.env.VITE_LOCAL_AUTH_BYPASS).toLowerCase() === 'true';
}

export const env: AppEnv = {
  apiBaseUrl: stripTrailingSlash(
    readString(import.meta.env.VITE_API_BASE_URL, '/api-not-configured'),
  ),
  cognitoAuthority: readString(import.meta.env.VITE_COGNITO_AUTHORITY),
  cognitoClientId: readString(import.meta.env.VITE_COGNITO_CLIENT_ID),
  cognitoDomain: stripTrailingSlash(readString(import.meta.env.VITE_COGNITO_DOMAIN)),
  redirectUri: readString(
    import.meta.env.VITE_REDIRECT_URI,
    `${window.location.origin}/auth/callback`,
  ),
  logoutUri: readString(import.meta.env.VITE_LOGOUT_URI, window.location.origin),
  authBypass: readAuthBypass(),
};

/** Lists the env vars that are required for real (non-bypass) auth but are missing. */
export function missingAuthConfig(target: AppEnv = env): string[] {
  if (target.authBypass) return [];
  const missing: string[] = [];
  if (!target.cognitoAuthority) missing.push('VITE_COGNITO_AUTHORITY');
  if (!target.cognitoClientId) missing.push('VITE_COGNITO_CLIENT_ID');
  if (!target.cognitoDomain) missing.push('VITE_COGNITO_DOMAIN');
  return missing;
}
